import { commonUtils } from '@/helpers/commonUtils'
import styles from './LoginPagePc.module.less'
import { useEffect, useRef, useState } from 'react'
import { Checkbox, Divider, message, Popover } from 'antd'
import AInput from '@/components/A/AInput/AInput'
import AButton from '@/components/A/AButton/AButton'
import AIcon from '@/components/A/AIcon/AIcon'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { PAGE_LINKS } from '@/router/pageConst'
import APassword from '@/components/A/APassword/APassword'
import { observer } from 'mobx-react'
import { userService } from '@/services/userService'
import { Trans, useTranslation } from 'react-i18next'
import { FormItem, FormRow } from '@/pages/ProjectCreatePage/components/StepOne/StepOne'
import { corpService } from '@/services/corpService'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import { INTERNAL_HOST } from '@/commonConst'
import { trackingService } from '@/services/trackingService'

const TO_GOOGLE_AUTH = '/ad_api/auth/google/oauth2_link'

type LoginMode = 'login' | 'sign'

function getLoginMode(state: unknown): LoginMode {
  const mode = (state as { mode?: LoginMode } | null | undefined)?.mode
  return mode === 'login' || mode === 'sign' ? mode : 'sign'
}

function LoginPagePc() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [mode, setMode] = useState<LoginMode>(() => getLoginMode(location.state))
  const [step, setStep] = useState<1 | 2>(1)

  const agreePrivay = useRef<boolean>(false)
  const [tooltip, setTooltip] = useState<{ visible: boolean }>({ visible: false })

  useEffect(() => {
    if (userService.userInfo && !userService.userInfo?.isExpert) {
      // navigate(PAGE_LINKS.project)
      navigate(PAGE_LINKS.index)
    }
  }, [userService.userInfo])

  useEffect(() => {
    setMode(getLoginMode(location.state))
  }, [location.state])

  useEffect(() => {
    if (mode === 'sign') {
      trackingService.trackRegisterPageView()
    }
  }, [mode])

  return (
    <div className={styles.loginPage} data-testid='login-page'>
      <div className={styles.loginPageContent}>
        <div className={styles.card}>
          <img
            className={styles.brand}
            src={commonUtils.getImagePath('loginPage/login-brand.png')}
            alt=''
          />
          {mode === 'sign' ? (
            <p style={{ marginTop: 20 }} className={styles.optionText}>
              {t('login.optionText1')}
              <span
                className={styles.pointText}
                data-testid='login-switch-to-login-btn'
                onClick={() => {
                  setMode('login')
                  setStep(1)
                }}
              >
                {t('login.loginText')}
              </span>
            </p>
          ) : (
            <p style={{ marginTop: 20 }} className={styles.optionText}>
              {t('login.optionText2')}
              <Trans
                i18nKey='login.signUpText'
                components={{
                  tmp: (
                    <span
                      className={styles.pointText}
                      data-testid='login-switch-to-signup-btn'
                      onClick={() => {
                        setMode('sign')
                        setStep(1)
                      }}
                    />
                  ),
                }}
              />
            </p>
          )}
          {step === 1 && (
            <div style={{ marginTop: 32 }}>
              <StepOne
                mode={mode}
                onNextStep={(url) => {
                  if (mode === 'sign' && !agreePrivay.current) {
                    setTooltip({ visible: true })
                    return
                  }
                  if (url) {
                    window.location.href = url
                  } else {
                    setStep(2)
                  }
                }}
              />
            </div>
          )}
          {step === 2 && <StepTwo mode={mode} />}
          {mode === 'sign' && step === 1 && (
            <PrivacyTips
              tooltip={tooltip}
              onAgreePrivacy={(checked) => {
                agreePrivay.current = checked
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function PrivacyTips(props: {
  tooltip: { visible: boolean }
  onAgreePrivacy: (checked: boolean) => void
}) {
  const { t, i18n } = useTranslation()
  const isJapanese = i18n.resolvedLanguage === 'ja' || i18n.language === 'ja'
  const [checked, setChecked] = useState(false)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (checked) {
      setVisible(false)
    }
    props.onAgreePrivacy(checked)
  }, [checked])

  useEffect(() => {
    setVisible(props.tooltip.visible)
  }, [props.tooltip])

  return (
    <div className={`${styles.privacyTips} ${isJapanese ? styles.japanesePrivacyTips : ''}`}>
      <Popover
        open={visible}
        placement='topLeft'
        arrow={{ pointAtCenter: true }}
        content={<div className={styles.privacyPopoverContent}>👇{t('login.privacyTips')}</div>}
      >
        <Checkbox
          data-testid='login-agree-checkbox'
          className={styles.checkbox}
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
      </Popover>
      <span
        className={`${styles.tips} ${isJapanese ? styles.japaneseTips : ''}`}
        data-testid='login-agree-tips'
        onClick={() => {
          setChecked(!checked)
        }}
      >
        {t('login.alertText1')}
        <Link to={PAGE_LINKS.termsOfService} target='_blank'>
          <span className={styles.pointText}>{t('login.termsOfService')} </span>
        </Link>
        {t('login.and')}
        <Link to={PAGE_LINKS.privacy} target='_blank'>
          <span className={styles.pointText}>{t('login.privacy')}</span>
        </Link>
        {isJapanese && <br />}
        {t('login.agreementSuffix', { defaultValue: '' })}
      </span>
    </div>
  )
}

function GoogleIcon() {
  return (
    <span className={styles.googleIcon}>
      <AIcon type='icon-line-global_google' width={18} height={18} />
    </span>
  )
}

function EmailIcon() {
  return <AIcon className={styles.emailIcon} type='icon-line-global_email' width={18} height={18} />
}

function StepOne(props: { mode: 'sign' | 'login'; onNextStep: (url: string) => void }) {
  const { t } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  return (
    <div className={styles.stepOne}>
      {!isInternal && (
        <>
          {/* <a style={{ textDecoration: 'none' }} href={TO_GOOGLE_AUTH}> */}
          <AButton testId='login-google-btn' size='LGR' type='primary' onClick={() => props.onNextStep(TO_GOOGLE_AUTH)}>
            <GoogleIcon />
            <span>{props.mode === 'login' ? t('login.googleLogin') : t('login.googleSignUp')}</span>
          </AButton>
          {/* </a> */}
          <Divider plain style={{ marginTop: 24, marginBottom: 24 }}>
            {t('login.or')}
          </Divider>
        </>
      )}
      <div className={styles.outlineBtn} onClick={() => props.onNextStep('')} data-testid='login-email-btn'>
        <EmailIcon />
        <span className={styles.text}>
          {props.mode === 'login' ? t('login.loginWithEmail') : t('login.signUpWithEmail')}
        </span>
      </div>
    </div>
  )
}

function StepTwo(props: { mode: 'sign' | 'login' }) {
  return (
    <div className={styles.stepTwo}>
      {props.mode === 'sign' && <SignStep />}
      {props.mode === 'login' && <LoginStep />}
    </div>
  )
}

export type ErrorKey = 'email'
  | 'email_registered'
  | 'password'
  | 'password_less_12'
  | 'password_contain_L&U&N'
  | 'r_password'
  | 'r_password_not_same'

interface ErrorMsgMap {
  email?: { key: ErrorKey, msg: string }
  password?: { key: ErrorKey, msg: string }
  rPassword?: { key: ErrorKey, msg: string }
}

function SignStep() {
  const { t } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  const [errorMsgsMap, setErrorMsgsMap] = useState<ErrorMsgMap>({})

  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2>(1)
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [rPassword, setRPassword] = useState<string | null>(null)
  const [verificationCode, setVerificationCode] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const validator = (
    email: string | null,
    password: string | null,
    rPassword: string | null,
    force?: boolean
  ) => {
    const map: ErrorMsgMap = {}
    if (force) {
      if (!email) {
        map.email = { key: 'email', msg: t('login.message.text1') }
      }
      if (!password) {
        map.password = { key: 'password', msg: t('login.message.text1') }
      }
      if (!rPassword) {
        map.rPassword = { key: 'r_password', msg: t('login.message.text1') }
      }
    } else {
      if (email === '') {
        map.email = { key: 'email', msg: t('login.message.text1') }
      }
      if (password === '') {
        map.password = { key: 'password', msg: t('login.message.text1') }
      }
      if (rPassword === '') {
        map.rPassword = { key: 'r_password', msg: t('login.message.text1') }
      }
    }
    if (email !== null && !commonUtils.emailFormatValidator(email || '')) {
      map.email = { key: 'email', msg: t('login.message.text1') }
    }
    if (password !== null && password.length < 12) {
      map.password = { key: 'password_less_12', msg: t('login.message.text6') }
    }
    if (password !== null) {
      const hasUpper = /[A-Z]/.test(password)
      const hasLower = /[a-z]/.test(password)
      const hasNumber = /[0-9]/.test(password)
      if (!hasUpper || !hasLower || !hasNumber) {
        map.password = { key: 'password_contain_L&U&N', msg: t('login.message.text7') }
      }
    }
    if (rPassword !== null && password !== null && password !== rPassword) {
      map.rPassword = { key: 'r_password_not_same', msg: t('login.inconsistentPassword') }
    }
    return map
  }

  const handleNext = async () => {
    if (step === 1) {
      const map = validator(email, password, rPassword, true)
      setErrorMsgsMap(map)
      if (Boolean(map.email) || Boolean(map.password) || Boolean(map.rPassword)) {
        return
      }
      setLoading(true)
      try {
        await api.auth.emailSignUp({ email: email!, password: password! }).send()
        setStep(2)
      } catch (e) {
        const error = e as ApiError
        if (error.statusCode === 40001) {
          setErrorMsgsMap((map) => ({ ...map, email: { key: 'email_registered', msg: t('login.message.text2') } }))
        } else if (error.statusCode === 40003) {
          setErrorMsgsMap((map) => ({ ...map, password: { key: 'password_contain_L&U&N', msg: t('login.message.text7') } }))
        } else if (error.statusCode === 40004) {
          message.error(t('login.message.requestLimit'))
        } else {
          message.error(error.statusMessage)
        }
      }
      setLoading(false)
    }
    if (step === 2) {
      try {
        if (!verificationCode) {
          return
        }
        setLoading(true)
        const { hasBindCorp } = await api.auth.verifyEmailCode(verificationCode).send()
        if (hasBindCorp) {
          navigate(PAGE_LINKS.index)
        } else {
          navigate(PAGE_LINKS.infoBind)
        }
      } catch (error) {
        const e = error as ApiError
        if (e.statusCode === 400) {
          message.error(e.statusMessage)
        } else {
          message.error(t('login.message.text3'))
        }
        console.error(error)
      }
      setLoading(false)
    }
  }


  return (
    <div className={styles.signStep}>
      {step === 1 && (
        <>
          <FormRow>
            <FormItem
              label={t('login.emailAddress')}
              error={Boolean(errorMsgsMap.email)}
              errorMsg={errorMsgsMap.email?.msg} >
              <AInput
                testId='login-signup-email-input'
                className={styles.inputWidth}
                placeholder={t('login.enterEmail')}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setErrorMsgsMap((map) => ({ ...map, email: undefined }))
                }}
                onBlur={() => {
                  const map = validator(email, password, rPassword)
                  if (email) {
                    api.auth
                      .checkEmailRegister(email)
                      .send()
                      .then(({ alreadyRegistered }) => {
                        if (alreadyRegistered) {
                          setErrorMsgsMap((map) => ({ ...map, email: { key: 'email_registered', msg: t('login.message.text2') } }))
                        }
                      })
                  } else {
                    setErrorMsgsMap(map)
                  }
                }}
              />
            </FormItem>
          </FormRow>
          <FormRow>
            <FormItem
              label={t('login.password')}
              error={Boolean(errorMsgsMap.password)}
              errorMsg={errorMsgsMap.password?.msg}
              errorMsgRender={(msg) => <span className={styles.errorMsg}>{msg}</span>}
            >
              <APassword
                testId='login-signup-password-input'
                className={styles.inputWidth}
                placeholder={t('login.enterPassword')}
                onChange={(e) => {
                  setPassword(e.target.value)
                  const map = validator(email, e.target.value, rPassword)
                  setErrorMsgsMap(map)
                  // setErrorMsgsMap((map) => ({ ...map, password: undefined }))
                }}
                onBlur={() => {
                  const _map = validator(email, password, rPassword)
                  setErrorMsgsMap((map) => ({ ...map, password: _map.password }))
                }}
              />
            </FormItem>
          </FormRow>
          <FormRow>
            <FormItem
              label={t('login.confirmPassword')}
              error={Boolean(errorMsgsMap.rPassword)}
              errorMsg={errorMsgsMap.rPassword?.msg}
            >
              <APassword
                testId='login-signup-confirm-password-input'
                className={styles.inputWidth}
                placeholder={t('login.confirmPassword')}
                onChange={(e) => {
                  setRPassword(e.target.value)
                  const _map = validator(email, password, e.target.value)
                  setErrorMsgsMap((map) => ({ ...map, rPassword: _map.rPassword }))
                }}
                onBlur={() => {
                  const _map = validator(email, password, rPassword)
                  setErrorMsgsMap((map) => ({ ...map, rPassword: _map.rPassword }))
                }}
              />
            </FormItem>
          </FormRow>
          <AButton
            testId='login-signup-next-btn'
            style={{ marginTop: 32 }}
            size='LGR'
            type='primary'
            loading={loading}
            onClick={handleNext}
          >
            {t('login.next')}
          </AButton>
        </>
      )}

      {step === 2 && (
        <>
          <FormRow>
            <FormItem
              label={t('login.verificationCode')}
              error={verificationCode === ''}
              errorMsg={t('login.emptyVerificationCode')}
            >
              <AInput
                testId='login-signup-verification-input'
                className={styles.inputWidth}
                placeholder={t('login.enterVerificationCode')}
                onChange={(e) => {
                  setVerificationCode(e.target.value)
                }}
              />
              {verificationCode !== '' && (
                <p className={styles.sendedDesc}>{t('login.sendedVerificationCode')}</p>
              )}
            </FormItem>
          </FormRow>
          <AButton
            testId='login-signup-verify-btn'
            style={{ marginTop: verificationCode !== '' ? 32 : 55 }}
            type='primary'
            size='LGR'
            loading={loading}
            onClick={handleNext}
          >
            {t('login.next')}
          </AButton>
        </>
      )}
      {!isInternal && (
        <>
          <Divider plain style={{ marginTop: 24, marginBottom: 24 }}>
            {t('login.or')}
          </Divider>
          <a style={{ textDecoration: 'none' }} href={TO_GOOGLE_AUTH}>
            <div className={styles.outlineBtn}>
              <GoogleIcon />
              <span className={styles.text}>{t('login.googleSignUp')}</span>
            </div>
          </a>
        </>
      )}
    </div>
  )
}

function LoginStep() {
  const { t } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  const [, setIsFrom] = useTypedLocalStorage('is_from', { defaultValue: '' })
  const emailValidator = (email: string | null, force?: boolean) => {
    if (!force && email === null) {
      return false
    }
    const emailIsEmpty = !email
    const emailIsValid = commonUtils.emailFormatValidator(email || '')
    return emailIsEmpty || !emailIsValid
  }

  const navigate = useNavigate()
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [errorKeys, setErrorKeys] = useState<Array<'email' | 'password'>>([])

  const handleNext = async () => {
    const keys = errorKeys
    if (emailValidator(email, true)) {
      keys.push('email')
      setErrorKeys(Array.from(new Set(keys)))
      return
    }
    try {
      await api.auth.emailLogin({ email: email!, password: password! }).send()
      await userService.refreshUserInfo()
      setIsFrom('')
      corpService.refreshFreeTrialStatus()
      // navigate(PAGE_LINKS.project)
      navigate(PAGE_LINKS.index)
    } catch (error) {
      const e = error as ApiError
      if (e.statusCode === 40001) {
        console.error('login error', e.statusMessage)
        keys.push('password')
        setErrorKeys(Array.from(new Set(keys)))
      } else if (e.statusCode === 40002) {
        message.warning(t('login.message.text4'))
        navigate(PAGE_LINKS.infoBind)
      }
    }
  }

  const passwordLabelRender = (label: string) => {
    return (
      <div style={{ display: 'flex', alignContent: 'center', justifyContent: 'space-between' }}>
        <label>{label}:</label>
        <Link to={PAGE_LINKS.resetPwd} className={styles.forgotPassword}>
          {t('login.forgotPassword')}
        </Link>
      </div>
    )
  }

  return (
    <div className={styles.loginStep}>
      <FormRow>
        <FormItem
          label={t('login.emailAddress')}
          error={errorKeys.includes('email')}
          errorMsg={t('login.message.text5')}
        >
          <AInput
            testId='login-email-input'
            className={styles.inputWidth}
            placeholder={t('login.enterEmail')}
            onChange={(e) => {
              setEmail(e.target.value)
              setErrorKeys((keys) => keys.filter((k) => k !== 'email'))
            }}
            onBlur={() => {
              let keys = errorKeys
              if (emailValidator(email)) {
                keys.push('email')
              } else {
                keys = keys.filter((k) => k !== 'email')
              }
              setErrorKeys(Array.from(new Set(keys)))
            }}
          />
        </FormItem>
      </FormRow>
      <FormRow>
        <FormItem
          label={t('login.password')}
          error={errorKeys.includes('password')}
          errorMsg={t('login.emailOrPasswordNotCorrect')}
          labelRender={passwordLabelRender}
        >
          <APassword
            testId='login-password-input'
            className={styles.inputWidth}
            placeholder={t('login.enterPassword')}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
        </FormItem>
      </FormRow>
      <AButton testId='login-submit-btn' type='primary' size='LGR' style={{ marginTop: 32 }} onClick={handleNext}>
        {t('login.next')}
      </AButton>
      {!isInternal && (
        <>
          <Divider plain style={{ marginTop: 24, marginBottom: 24 }}>
            {t('login.or')}
          </Divider>
          <a style={{ textDecoration: 'none' }} href={TO_GOOGLE_AUTH}>
            <div className={styles.outlineBtn}>
              <GoogleIcon />
              <span className={styles.text}>{t('login.googleLogin')}</span>
            </div>
          </a>
        </>
      )}
    </div>
  )
}

export default observer(LoginPagePc)
